import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { useShowroomTicker } from '../../hooks/useInventory';
import { usePreferences } from '../../hooks/usePreferences';
import { useVault } from '../../context/VaultContext';
import type { ShowroomTickerItem } from '../../api/client';

const CONDITION_COLORS: Record<string, string> = {
  mint: 'var(--success)',
  excellent: 'var(--accent)',
  good: 'var(--text-secondary)',
  fair: 'var(--warning)',
  poor: 'var(--error)',
  parts: 'var(--error)',
};

// Constant scroll speed in pixels per second
const SCROLL_SPEED = 60;

export function Ticker() {
  const { preferences } = usePreferences();
  const { activeVault } = useVault();
  const { data } = useShowroomTicker(activeVault?.vaultType);
  const items: ShowroomTickerItem[] = data?.data ?? [];
  const [paused, setPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  const measure = useCallback(() => {
    if (contentRef.current) {
      // Content is duplicated, so half the scrollWidth is the real content width
      const contentWidth = contentRef.current.scrollWidth / 2;
      setDuration(contentWidth / SCROLL_SPEED);
    }
  }, []);

  useEffect(() => {
    measure();
  }, [items, measure]);

  if (!preferences.showTicker || items.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '28px',
        background: 'var(--bg-primary)',
        borderTop: '1px solid var(--border)',
        overflow: 'hidden',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        ref={contentRef}
        style={{
          whiteSpace: 'nowrap',
          fontFamily: 'var(--font-mono)',
          fontSize: '0.6875rem',
          animation: duration > 0 ? `tickerScroll ${duration}s linear infinite` : 'none',
          animationPlayState: paused ? 'paused' : 'running',
          paddingLeft: '100%',
        }}
      >
        {[...items, ...items].map((item, i) => {
          const condColor = CONDITION_COLORS[item.condition] ?? '#888';
          const price = item.acquisitionPrice
            ? parseFloat(item.acquisitionPrice).toLocaleString('en-US', { style: 'currency', currency: item.currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 })
            : null;

          return (
            <Link
              key={i}
              to="/inventory/$itemId"
              params={{ itemId: item.id }}
              search={{ from: undefined }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <span style={{ color: 'var(--accent)' }}>{item.name}</span>
              {' '}
              <span style={{ color: 'var(--text-muted)' }}>{item.platform}</span>
              {' \u2022 '}
              <span style={{ color: condColor }}>{item.condition}</span>
              {price && (
                <>
                  {' \u2022 '}
                  <span style={{ color: 'var(--warning)' }}>{price}</span>
                </>
              )}
              {item.ownerDisplayName && (
                <>
                  {' \u2022 '}
                  <span style={{ color: 'var(--text-muted)' }}>{item.ownerDisplayName}</span>
                </>
              )}
              <span style={{ color: 'var(--border)', margin: '0 1rem' }}>{'\u2502'}</span>
            </Link>
          );
        })}
      </div>
      <style>{`
        @keyframes tickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
